import React from "react";
import { Draggable } from "react-beautiful-dnd";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";

import { initCurate } from "./itemTypes";
import { ExpandButton } from "../Components/ExpandButton";
import { getType, types } from "./itemTypes";
import ComputationCurate from "./Components/ComputationCurate";
import InputChoice from "./Choice/InputChoice";

function QuestionContent({
  question,
  questionLeaf,
  handleQuestionTreeApi,
  isParent,
  isExpanded,
  disabled,
  provided,
  isCurate,
  updateHandler,
  selected,
  onMouseDown,
}) {
  const { t } = useTranslation();
  const selectedId = selected.split("#")?.[1] ?? "";

  // curate
  const isCurateQuestion = isCurate && question?.original?.itemExtra?.curate;
  const curate = question?.original?.itemExtra?.curate ?? initCurate;
  const showCheckbox = isCurateQuestion && Boolean(curate.show);
  const hideTree = isCurateQuestion && Boolean(curate.hideTree);
  const fullQuestionEditable = curate?.fullQuestionEditable ?? false; // edit the fullquestion curate mode
  // multiple choice curate
  const showOptions = curate?.showOptions ?? false; // show multiple choice options
  const allowOptionAdd = curate?.allowOptionAdd ?? false; // show the add button
  const allowOptionEditFrom = curate?.allowOptionEditFrom; // which options are editable
  const disabledOptionIds =
    allowOptionEditFrom === undefined
      ? (question?.options ?? []).map((option) => option.id)
      : allowOptionEditFrom === 0
      ? []
      : question.options
          .filter((_, i) => i < allowOptionEditFrom - 1)
          .map((option) => option.id);
  return (
    <Box display="flex" width="100%">
      {/** Label and expand button */}
      <Box
        width={92}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        {/** Checkbox or info icon */}
        <Box margin={"0"}>
          <div {...provided.dragHandleProps}></div>
          {Boolean(curate.allowHide) ? (
            <Box textAlign="left">
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="large" />}
                checkedIcon={<CheckBoxIcon fontSize="large" />}
                name="curate"
                color="primary"
                checked={question.checked}
                onChange={(event) =>
                  updateHandler({ checked: event.target.checked })
                }
                disabled={disabled}
              />
            </Box>
          ) : (
            <Box textAlign="left" sx={{ color: "text.secondary" }}>
              <InfoOutlinedIcon fontSize="large" />
            </Box>
          )}
        </Box>
        {/** Expand button */}
        <Box>
          {!hideTree ? (
            <ExpandButton
              expand={isExpanded}
              onClick={() =>
                handleQuestionTreeApi({
                  type: "TOGGLE_EXPAND_QUESTION",
                  payload: {
                    key: questionLeaf.key,
                  },
                })
              }
              aria-expanded={isExpanded}
              aria-label="show conditional questions"
              size="small"
              fontSize="small"
              sx={{ display: isParent ? "flex" : "none" }}
            />
          ) : null}
        </Box>
      </Box>
      {/** Vertical divider */}
      <Box padding={1} />
      <Box sx={{ borderRight: "1px solid rgba(0, 0, 0, 0.2)" }} />
      <Box padding={1} />
      {/** Question */}
      {showCheckbox ? (
        <Box display="block" textAlign="left" width="100%">
          <Typography>
            {curate.title === "" ? question.label : curate.title}
          </Typography>
          <Typography color="textSecondary">{curate.info}</Typography>
          {/* edit fullQuestion in curate view */}
          {fullQuestionEditable && question.type !== "computation" ? (
            <TextField
              variant="standard"
              value={question.title}
              placeholder={
                getType(question.type) !== types.unknown
                  ? t(`editor.questionTypes.${question.type}.placeholder`)
                  : `${question.type} ${t(`editor.placeholders.item`)}`
              }
              onChange={(e) => updateHandler({ title: e.target.value })}
              //autoFocus={selected === "question"}
              sx={{ width: "100%" }}
              InputProps={{
                startAdornment: (
                  <EditIcon color="disabled" sx={{ marginRight: 1 }} />
                ),
              }}
              disabled={disabled}
            />
          ) : null}
          {showOptions ? (
            <InputChoice
              question={question}
              updateHandler={updateHandler}
              disabledOptionIds={disabledOptionIds}
              isDropDisabled={true}
              disabled={disabled}
              allowOptionAdd={allowOptionAdd}
              isSelected={true}
              selectedId={selectedId}
              onMouseDown={onMouseDown}
            />
          ) : null}
          {fullQuestionEditable && question.type === "computation" ? (
            <ComputationCurate
              curate={curate}
              question={question}
              updateHandler={updateHandler}
              disabled={disabled}
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default function CurateQuestion(props) {
  const {
    indentLevel,
    selected,
    questionLeaf,
    disabled = false,
    isExpanded,
    index,
    handleQuestionTreeApi,
    isCurate,
  } = props;
  const indentMargin = (indentLevel.split(".").length - 2) * 4;
  const question = { ...questionLeaf.value, items: [] };
  const isParent = questionLeaf.children.length > 0;

  // Card area is pressed
  const onMouseDownHandler = (optionId) => {
    handleQuestionTreeApi({
      type: "SELECT_QUESTION",
      payload:
        optionId === undefined
          ? questionLeaf.key
          : `${questionLeaf.key}#${optionId}`,
    });
  };

  const updateHandler = (data) => {
    const updatedQuestion = { ...question, ...data };
    handleQuestionTreeApi({
      type: "UPDATE_QUESTION",
      payload: {
        key: questionLeaf.key,
        value: updatedQuestion,
      },
    });
  };

  return (
    <Draggable
      draggableId={question.id + ""}
      index={index}
      isDragDisabled={disabled}
    >
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          id={question.id}
          paddingBottom={2}
          paddingLeft={indentMargin}
          onMouseDown={onMouseDownHandler}
          onFocus={onMouseDownHandler}
        >
          <Card
            sx={{
              display: "flex",
              background: "",
              borderBottom:
                isParent && !isExpanded
                  ? "7px solid rgba(0,0,0,0.12)"
                  : "1px solid rgba(0,0,0,0.12)",
              padding: 2,
            }}
            variant="outlined"
          >
            <QuestionContent
              question={question}
              questionLeaf={questionLeaf}
              handleQuestionTreeApi={handleQuestionTreeApi}
              isParent={isParent}
              selected={selected}
              isExpanded={isExpanded}
              disabled={disabled}
              provided={provided}
              isCurate={isCurate}
              updateHandler={updateHandler}
              onMouseDown={onMouseDownHandler}
            />
          </Card>
          <Box padding={1.5} />
        </Box>
      )}
    </Draggable>
  );
}

/* function questionsAreEqual(prevQuestions, nextQuestions) {
  return (
    prevQuestions.position === nextQuestions.position &&
    prevQuestions.isExpanded === nextQuestions.isExpanded &&
    JSON.stringify(prevQuestions.question) ===
      JSON.stringify(nextQuestions.question) &&
    prevQuestions.disabled === nextQuestions.disabled
  );
}

export default React.memo(StaticCurateQuestion, questionsAreEqual); */
